import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, UserPlus, User, FileText, ShieldCheck, Loader2, AlertCircle,
  CheckCircle, AlertTriangle, Users, Briefcase, GraduationCap, Award,
} from "lucide-react";

import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";
import {
  useGetProbationerApplicationDetailsQuery,
  useAdminUpdateProbationerStatusMutation,
  useAdminUpdateProbationerEmailMutation,
  useProbationerReviewQueueQuery,
  useAssignProbationerReviewerMutation,
} from "../api/probationerApplicationApi";
import {
  isAwaitingEndorsements,
  statusColors,
  statusLabel,
} from "../utils/applicationStatus";

/**
 * Secretariat review of one ENTRY (probationer) application.
 *
 * The same SOP as firm registration, and the same screen shape as
 * AdminUpgradeApplicationDetailPage. What the secretariat can do here depends on
 * where the application stands:
 *
 *   awaiting endorsements  nothing to do but chase — the chapter and the two
 *                          referees have it, and it comes here on its own when
 *                          the last of them answers
 *   secretariat review     vet it, then either send it to the Membership
 *                          Committee or return it with a note
 *   with committee         only return it; the committee decides it
 *
 * Approve and reject are NOT secretariat actions at any rung. The Membership
 * Committee chairman finalises, having read the assigned member's
 * recommendation.
 */

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function StatusBadge({ status }: { status?: string }) {
  const s = String(status ?? "pending").toLowerCase();
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold border rounded-full ${statusColors(s).pill}`}
      title={s}
    >
      {statusLabel(s)}
    </span>
  );
}

/** One line of the readiness checklist: met, or still owing. */
function Prerequisite({ met, label }: { met: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {met ? (
        <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
      ) : (
        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
      )}
      <span className={met ? "text-gray-700" : "text-amber-700"}>{label}</span>
    </div>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-gray-50/70">
        <span className="text-gray-500">{icon}</span>
        <span className="text-sm font-semibold text-gray-700">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</div>
      <div className="text-sm text-gray-800 break-words">
        {value === null || value === undefined || value === "" ? "—" : value}
      </div>
    </div>
  );
}

/**
 * The applicant's email, correctable in place.
 *
 * Here rather than behind a separate screen because of what it is for: a
 * member whose recorded address is wrong applies again with their real one,
 * and the secretariat cannot then fix the member record — the address is held
 * by this very application. Moving it is the only way through, and the person
 * doing it is already looking at this page.
 *
 * It changes the LOGIN ACCOUNT too, which the confirmation says out loud. That
 * is not a detail: freeing the address on the application alone would leave the
 * account still holding it and the member record still unfixable.
 */
function EmailField({ application }: { application: any }) {
  const current = application?.email ?? application?.user?.email ?? "";
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<string>(current);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [save, { isLoading }] = useAdminUpdateProbationerEmailMutation();

  const submit = async () => {
    setMsg(null);
    const email = value.trim();

    if (!email) return setMsg({ ok: false, text: "Enter an email address." });
    if (email === current) {
      setEditing(false);
      return;
    }

    try {
      const res: any = await save({ id: application.id, email }).unwrap();
      setMsg({ ok: true, text: res?.message ?? "Email updated." });
      setEditing(false);
    } catch (e: any) {
      setMsg({
        ok: false,
        text:
          e?.data?.errors?.email?.[0] ||
          e?.data?.message ||
          "Could not update the email address.",
      });
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Email</div>
        {!editing && (
          <button
            onClick={() => {
              setValue(current);
              setMsg(null);
              setEditing(true);
            }}
            className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 hover:underline"
          >
            Change
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-1 space-y-2">
          <input
            type="email"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
            placeholder="name@example.com"
          />
          <p className="text-[11px] text-gray-400">
            Changes the application and the applicant&apos;s sign-in address.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={submit}
              disabled={isLoading}
              className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50"
            >
              {isLoading ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setMsg(null);
              }}
              className="px-3 py-1.5 rounded-lg border text-xs hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-800 break-words">{current || "—"}</div>
      )}

      {msg && (
        <p className={`mt-1 text-[11px] ${msg.ok ? "text-emerald-600" : "text-rose-600"}`}>
          {msg.text}
        </p>
      )}
    </div>
  );
}

function Table({ heads, rows }: { heads: string[]; rows: React.ReactNode[][] }) {
  if (!rows.length) {
    return <p className="text-sm text-gray-400">Nothing recorded.</p>;
  }
  return (
    <div className="border rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-gray-400 border-b">
            {heads.map((h) => (
              <th key={h} className="px-3 py-2">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j} className="px-3 py-2 text-gray-700">{c ?? "—"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const STORAGE_BASE = "https://api.niqsng.org/storage";

/** Passes absolute URLs through; strips a leading slash so paths don't double up. */
const storageUrl = (p?: string | null): string | null =>
  !p ? null : String(p).startsWith("http") ? String(p) : `${STORAGE_BASE}/${String(p).replace(/^\//, "")}`;

/**
 * Subjects are stored as an object map — {"Mathematics":"A1","English":"B2"} —
 * not an array of rows. Rendered as one chip per subject so grades can be read
 * at a glance rather than as a run-on string.
 */
function SubjectGrades({ subjects }: { subjects: any }) {
  if (!subjects || typeof subjects !== "object") {
    return <span className="text-gray-300">—</span>;
  }

  const entries = Object.entries(subjects as Record<string, any>);
  if (!entries.length) return <span className="text-gray-300">—</span>;

  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([subject, grade]) => (
        <span
          key={subject}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 text-[11px]"
        >
          <span className="text-gray-600">{subject}</span>
          <span className="font-bold text-gray-900">{String(grade)}</span>
        </span>
      ))}
    </div>
  );
}

/** A link to any uploaded document, or a dash when nothing was attached. */
function FileLink({ path }: { path?: string | null }) {
  const url = storageUrl(path);
  if (!url) return <span className="text-gray-300">—</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
      View
    </a>
  );
}

/**
 * The scanned result. Uploading it is compulsory, so it is shown rather than
 * hidden behind a link — a reviewer checking grades against the slip should not
 * have to open a new tab for every result.
 */
function ResultFile({ path }: { path?: string | null }) {
  const url = storageUrl(path);
  if (!url) {
    return <span className="text-xs font-semibold text-rose-600">Not uploaded</span>;
  }

  const isPdf = /\.pdf(\?|$)/i.test(url);

  if (isPdf) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-indigo-600 hover:underline text-sm"
      >
        <FileText className="w-4 h-4" /> Open PDF
      </a>
    );
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" title="Open full size">
      <img
        src={url}
        alt="Scanned result"
        className="w-40 h-28 object-cover rounded-lg border hover:opacity-90 transition-opacity"
      />
    </a>
  );
}

/** The append-only trail of what has been done to this application. */
function ReviewTimeline({ reviews }: { reviews?: any[] }) {
  if (!reviews?.length) return null;
  return (
    <Section icon={<FileText className="w-4 h-4" />} title="Review trail">
      <ol className="space-y-3">
        {reviews.map((r: any) => (
          <li key={r.id} className="flex gap-3">
            <div className="mt-1 w-2 h-2 rounded-full bg-gray-300 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-800">
                {String(r.decision ?? "").replace(/_/g, " ")}
              </div>
              {r.comment ? <div className="text-sm text-gray-600">{r.comment}</div> : null}
              <div className="text-[11px] text-gray-400">{fmtDate(r.created_at)}</div>
            </div>
          </li>
        ))}
      </ol>
    </Section>
  );
}

export default function AdminProbationerApplicationReviewPage() {
  const { id = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: app, isLoading, isError, refetch } =
    useGetProbationerApplicationDetailsQuery(id, { skip: !id });
  const [update, { isLoading: saving }] = useAdminUpdateProbationerStatusMutation();

  const dietId = (app as any)?.logbook_diet_id ?? "";

  // The Membership Committee panel for this application's diet — the same list
  // the diet's assignment screen hands work out from, so the two cannot offer
  // different people.
  // Deliberately the whole diet, not this application's category: the list
  // wanted here is who is on the committee, and scoping it would only narrow
  // the load counts shown beside each name.
  const { data: queue } = useProbationerReviewQueueQuery({ dietId }, { skip: !dietId });
  const [assign, { isLoading: assigning }] = useAssignProbationerReviewerMutation();

  const [remark, setRemark] = useState("");
  const [reviewerId, setReviewerId] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if ((app as any)?.rejected_reason) setRemark((app as any).rejected_reason);
  }, [(app as any)?.rejected_reason]);

  // People who currently sit on the committee. Former members are carried by
  // the queue so their existing workload still shows — they are not somebody
  // new work can be given to.
  const panel: any[] = ((queue as any)?.members ?? []).filter((m: any) => !m.is_former);

  /* Everything that must hold before an application can be handed to the
     committee — the same three the server checks in assignBulk(), read here so
     the screen refuses for the reason the server would rather than offering a
     picker that 422s. Computed from `app` before the early returns below, since
     committeePicker is built at this point in the render.

     It matters for more than tidiness: an imported or legacy application can sit
     at `under_review` carrying no endorsements at all, because it was admitted
     without ever climbing the rung that collects them. Those are exactly the
     rows that looked assignable and were not. */
  const chapterEndorsed = !!(app as any)?.chapter_accepted_at;
  const approvedReferees = ((app as any)?.referees ?? []).filter(
    (r: any) => String(r?.decision).toLowerCase() === "approved",
  ).length;
  const feeSettled = (app as any)?.fee_settled !== false;
  const reviewReady = feeSettled && chapterEndorsed && approvedReferees >= 2;

  const assignBlockers: string[] = [
    ...(feeSettled ? [] : ["the application fee is unsettled"]),
    ...(chapterEndorsed ? [] : ["the chapter has not endorsed it"]),
    ...(approvedReferees >= 2
      ? []
      : [`only ${approvedReferees} of 2 referees have accepted`]),
  ];

  /** Hand the application to one committee member — this IS the handover. */
  const assignToCommittee = async () => {
    setMsg(null);
    setErr(null);
    if (!reviewerId) {
      setErr("Choose the committee member who will review this application.");
      return;
    }
    try {
      const res: any = await assign({
        dietId,
        member_id: reviewerId,
        application_ids: [id],
      }).unwrap();
      setMsg(res?.message ?? "Sent to the Membership Committee.");
      setReviewerId("");
      refetch();
    } catch (e: any) {
      setErr(e?.data?.message || "Could not assign this application.");
    }
  };

  /**
   * The handover control. Shown at secretariat review — where naming the
   * reviewer is how an application is sent — and again on one already with the
   * committee that nobody has picked up, so it can be given an owner without a
   * trip to the diet screen.
   */
  const committeePicker = (
    <div className="rounded-lg border border-gray-100 p-3 space-y-2">
      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
        Send to Membership Committee
      </label>
      {assignBlockers.length > 0 ? (
        <p className="text-[11px] text-amber-600 leading-relaxed">
          This cannot go to the committee yet — {assignBlockers.join(", and ")}. Only an
          application the chapter and both referees have accepted can be assigned.
        </p>
      ) : panel.length > 0 ? (
        <>
          <select
            value={reviewerId}
            onChange={(e) => setReviewerId(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-purple-100"
          >
            <option value="">Choose a committee member…</option>
            {panel.map((m: any) => (
              <option key={m.member_id} value={m.member_id}>
                {m.name}
                {m.committee_role ? ` · ${m.committee_role}` : ""}
                {typeof m.pending_count === "number" ? ` · ${m.pending_count} pending` : ""}
              </option>
            ))}
          </select>
          <button
            onClick={assignToCommittee}
            disabled={assigning || !reviewerId}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
            Assign &amp; send to committee
          </button>
          <p className="text-[10px] text-gray-400 leading-relaxed">
            They review it and recommend; the committee chairman makes the final decision.
            Handing out several at once is done from the diet's assignment screen.
          </p>
        </>
      ) : (
        <>
          {/* No "send it anyway" button. Sending to the committee IS naming a
              reviewer — the server refuses the bare status move for the same
              reason — so with nobody to name, the application stays with the
              secretariat and what needs fixing is said plainly instead. */}
          <p className="text-[11px] text-amber-600 leading-relaxed">
            {(queue as any)?.message ??
              "No Membership Committee member is available to assign."}{" "}
            It stays with the secretariat until there is someone to give it to.
          </p>
          <Link
            to="/executive-sets"
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-indigo-700 hover:underline"
          >
            <Users className="w-3.5 h-3.5" /> Set up the Membership Committee
          </Link>
        </>
      )}
    </div>
  );

  const act = async (target: string) => {
    setMsg(null);
    setErr(null);
    if (target === "returned" && !remark.trim()) {
      setErr("Add a note telling the applicant what to correct before returning it.");
      return;
    }
    try {
      // The server says what actually happened — where it went, and what it is
      // still waiting on. Repeating a guess here is how the two drift apart.
      const res: any = await update({ id, status: target, remark: remark || undefined }).unwrap();
      setMsg(res?.message ?? "Status updated.");
      refetch();
    } catch (e: any) {
      setErr(e?.data?.message || "Could not update status.");
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 py-24 flex flex-col items-center gap-3 text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin" />
        <p className="text-sm">Loading application…</p>
      </div>
    );
  }

  if (isError || !app) {
    return (
      <div className="max-w-4xl mx-auto p-6 py-20 text-center space-y-3">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
        <p className="text-sm text-red-500">Could not load this application.</p>
      </div>
    );
  }

  const a: any = app;

  // chapterEndorsed / approvedReferees / feeSettled / reviewReady are computed
  // above, alongside committeePicker which needs them — the same list the server
  // refuses on, so the screen and the refusal can never disagree. The fee is
  // settled against the applicant's signup invoice rather than this record, so
  // it is read from what the API attaches.

  const status = String(a.status ?? "").toLowerCase();
  const awaitingEndorsements = isAwaitingEndorsements(status);
  // `submitted` is what the retired auto-advance wrote for this same rung.
  const atSecretariat = status === "under_review" || status === "submitted";
  const atCommittee = status === "with_committee";

  /* Back goes BACK — to wherever this application was opened from.
     This screen is reached from several places now (the category list, the
     review-assignment queue, a panel member's own list), and a fixed
     destination sent everyone to the category list, which for anybody
     working the assignment queue meant losing their place every time they
     looked at an application.

     Three readings, in order of how much they actually know:
       1. the sender said where it came from, and what to call it;
       2. there is an in-app page behind this one — go back to it;
       3. nothing to go back to (a pasted link, a new tab), so fall back to
          the application's own category list. */
  const from = (location.state as any)?.from as
    | { pathname?: string; label?: string }
    | undefined;

  // react-router stamps its position in the history stack. 0 means this page
  // IS the first entry — there is no previous page in the app to return to,
  // and navigate(-1) would leave the portal altogether.
  const hasHistory = Number((window.history.state as any)?.idx ?? 0) > 0;

  const backTo = a.logbook_diet_id && a.application_category_id
    ? `/probationer-applications/admin/submissions/diets/${a.logbook_diet_id}/category/${a.application_category_id}`
    : "/probationer-applications/admin";

  const backLabel = a.category?.name
    ? `Back to ${a.category.name} applications`
    : "All entry applications";

  const backClass = "inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800";

  return (
    <PageGate perm={PERMS.application_diet.allCategoriesAccess}>
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
        {from?.pathname ? (
          <Link to={from.pathname} className={backClass}>
            <ArrowLeft className="w-4 h-4" />
            Back to {from.label ?? "where you were"}
          </Link>
        ) : hasHistory ? (
          <button type="button" onClick={() => navigate(-1)} className={backClass}>
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        ) : (
          <Link
            to={backTo}
            state={a.category?.name ? { categoryName: a.category.name } : undefined}
            className={backClass}
          >
            <ArrowLeft className="w-4 h-4" />
            {backLabel}
          </Link>
        )}

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-indigo-600 to-blue-700 text-white p-6 shadow-lg">
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 text-indigo-100 text-xs font-semibold mb-1.5 bg-white/10 px-2.5 py-1 rounded-full">
                <UserPlus className="w-3.5 h-3.5" /> Entry Application
              </div>
              <h1 className="text-2xl font-bold">
                {[a.title, a.surname, a.other_names].filter(Boolean).join(" ") || a.user?.name}
              </h1>
              <p className="text-sm text-indigo-100 mt-1">
                {a.category?.name ?? "—"} · submitted {fmtDate(a.created_at)}
                {a.diet?.title ? ` · ${a.diet.title}` : ""}
              </p>
            </div>
            <StatusBadge status={a.status} />
          </div>
        </div>

        {/* The receipt, before anything else. A reviewer's first question is
            whether the fee is settled, and until now the page could only say
            yes or no — not what to quote back to an applicant who insists they
            have paid. */}
        <FeeReceipt receipt={(a as any).fee_payment} settled={(a as any).fee_settled} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <Section icon={<User className="w-4 h-4" />} title="Applicant">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Name" value={[a.title, a.surname, a.other_names].filter(Boolean).join(" ")} />
                <EmailField application={a} />
                <Field label="Phone" value={a.phone} />
                <Field label="Gender" value={a.gender} />
                <Field label="Date of birth" value={a.date_of_birth} />
                <Field label="Nationality" value={a.nationality} />
                <Field label="State" value={a.state?.name} />
                <Field label="LGA" value={a.lga?.name} />
                <Field label="Chapter" value={a.chapter?.name ?? a.chapter_other} />
                <Field label="Sector" value={a.sector?.name} />
                <Field label="Residential address" value={a.residential_address} />
                <Field label="Postal address" value={a.postal_address} />
              </div>
            </Section>

            <Section icon={<GraduationCap className="w-4 h-4" />} title="Qualifications">
              <Table
                heads={["Institution", "Qualification", "Year", "Certificate"]}
                rows={(a.qualifications ?? []).map((r: any) => [
                  r.institution,
                  r.qualification,
                  r.year,
                  <FileLink path={r.cert_file ?? r.certificate_path} />,
                ])}
              />
            </Section>

            <Section icon={<FileText className="w-4 h-4" />} title="O'Level results">
              {(a.o_level_results ?? []).length === 0 ? (
                <p className="text-sm text-gray-400">Nothing recorded.</p>
              ) : (
                <div className="space-y-4">
                  {(a.o_level_results ?? []).map((r: any, i: number) => (
                    <div key={r.id ?? i} className="border rounded-lg p-4">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 flex-1 min-w-[16rem]">
                          <Field label="Exam" value={String(r.exam_type ?? "").toUpperCase()} />
                          <Field label="Year" value={r.exam_year} />
                          <Field label="Exam no." value={r.exam_number} />
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                            Scanned result
                          </div>
                          <ResultFile path={r.result_file} />
                        </div>
                      </div>

                      <div className="mt-3">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">
                          Subjects
                        </div>
                        <SubjectGrades subjects={r.subjects} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={<Award className="w-4 h-4" />} title="Professional memberships">
              <Table
                heads={["Body", "Stage passed", "Certificate"]}
                rows={(a.professional_memberships ?? []).map((r: any) => [
                  r.body_name,
                  r.stage_passed,
                  <FileLink path={r.certificate_path ?? r.cert_file} />,
                ])}
              />
            </Section>

            <Section icon={<Briefcase className="w-4 h-4" />} title="Work experience">
              <Table
                heads={["Organisation", "Position", "From", "To", "Responsibilities"]}
                rows={(a.work_experiences ?? []).map((r: any) => [
                  r.organization, r.position, r.start_date, r.end_date ?? "Present", r.responsibilities,
                ])}
              />
            </Section>

            <Section icon={<FileText className="w-4 h-4" />} title="Seminars & workshops">
              <Table
                heads={["Title", "Date", "Location"]}
                rows={(a.seminars ?? []).map((r: any) => [r.title, r.date, r.location])}
              />
            </Section>

            <Section icon={<Users className="w-4 h-4" />} title="Referees">
              <Table
                heads={["Referee", "Membership No.", "Relationship", "Decision"]}
                rows={(a.referees ?? []).map((r: any) => [
                  r.member
                    ? [r.member.surname, r.member.firstname, r.member.othernames].filter(Boolean).join(" ")
                    : r.member_id,
                  r.member?.membership_no,
                  r.relationship,
                  String(r.decision ?? "pending"),
                ])}
              />
            </Section>

            <ReviewTimeline reviews={a.reviews} />
          </div>

          {/* Review action */}
          <div className="space-y-5">
            <div className="bg-white border rounded-xl shadow-sm overflow-hidden sticky top-4">
              <div className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5">
                <ShieldCheck className="w-4 h-4" />
                <span className="text-sm font-semibold">Review decision</span>
              </div>
              <div className="p-4 space-y-3">
                {/* Where the application stands, in one line. */}
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
                    Stage
                  </p>
                  <p className="text-sm font-semibold text-gray-800">{statusLabel(status)}</p>
                  <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">
                    {awaitingEndorsements &&
                      "With the chapter and the referees. It comes to the secretariat on its own once all three have accepted."}
                    {atSecretariat &&
                      "With the secretariat. Send it to the Membership Committee, or return it to the applicant."}
                    {atCommittee &&
                      "With the Membership Committee. Only they approve or reject it."}
                  </p>
                </div>

                {/* Prerequisite checklist — what has to be in before it moves */}
                {awaitingEndorsements && (
                  <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Waiting on
                    </p>
                    <Prerequisite met={feeSettled} label="Application fee settled" />
                    <Prerequisite met={chapterEndorsed} label="Chapter endorsement" />
                    <Prerequisite
                      met={approvedReferees >= 2}
                      label={`Referees approved (${approvedReferees}/2)`}
                    />
                  </div>
                )}

                {(awaitingEndorsements || atSecretariat || atCommittee) && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">
                      Message to applicant
                    </label>
                    <textarea
                      value={remark}
                      onChange={(e) => setRemark(e.target.value)}
                      rows={4}
                      placeholder="Required when returning for editing…"
                      className="mt-1 w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100 resize-none"
                    />
                  </div>
                )}

                {msg && (
                  <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <CheckCircle className="w-3.5 h-3.5" /> {msg}
                  </div>
                )}
                {err && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {err}
                  </div>
                )}

                {/* ── Awaiting endorsements: chase, or send it back ──
                    There is no "mark vetted" button any more. The application
                    moves itself the moment the last endorsement lands, so the
                    only thing the secretariat can usefully do at this rung is
                    return it. The forward button appears only in the case the
                    automation cannot cover: endorsements recorded before it
                    existed. */}
                {awaitingEndorsements && (
                  <div className="space-y-2">
                    {reviewReady ? (
                      <button
                        onClick={() => act("under_review")}
                        disabled={saving}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                        Move to secretariat review
                      </button>
                    ) : (
                      <p className="text-[11px] text-amber-600 text-center">
                        Nothing to do here yet — it comes to the secretariat on its own once
                        everything above is in.
                      </p>
                    )}
                    <button
                      onClick={() => act("returned")}
                      disabled={saving}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-amber-300 text-amber-700 text-sm font-semibold hover:bg-amber-50 disabled:opacity-50 transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" /> Return for editing
                    </button>
                  </div>
                )}

                {/* ── Secretariat review: hand it to a committee member, or
                    send it back. Naming the reviewer IS the handover — an
                    application pushed to the committee with nobody on it is
                    work in a queue no one is answerable for. */}
                {atSecretariat && (
                  <div className="space-y-2">
                    {committeePicker}
                    <button
                      onClick={() => act("returned")}
                      disabled={saving}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-amber-300 text-amber-700 text-sm font-semibold hover:bg-amber-50 disabled:opacity-50 transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" /> Return for editing
                    </button>
                  </div>
                )}

                {/* ── With the committee: theirs to decide ── */}
                {atCommittee && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-gray-100 p-3">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                        Committee reviewer
                      </p>
                      {a.reviewer ? (
                        <p className="text-sm font-semibold text-gray-800">
                          {[a.reviewer.surname, a.reviewer.firstname, a.reviewer.othernames]
                            .filter(Boolean)
                            .join(" ")}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">
                          Nobody has picked this up yet — name the member who will review it.
                        </p>
                      )}
                      {a.logbook_diet_id && (
                        <Link
                          to={`/probationer-applications/admin/submissions/diets/${a.logbook_diet_id}/assignments`}
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 hover:underline"
                        >
                          <Users className="w-3.5 h-3.5" /> Review assignment
                        </Link>
                      )}
                      <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">
                        The committee chairman makes the final approve/reject decision after the
                        assigned member reviews.
                      </p>
                    </div>

                    {/* Handed over but unclaimed — give it an owner here rather
                        than sending the admin to another screen for one row. */}
                    {!a.reviewer && committeePicker}

                    <button
                      onClick={() => act("returned")}
                      disabled={saving}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-amber-300 text-amber-700 text-sm font-semibold hover:bg-amber-50 disabled:opacity-50 transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" /> Return for editing
                    </button>
                  </div>
                )}

                {/* ── Terminal / waiting states ── */}
                {["approved", "rejected", "returned", "pending"].includes(status) && (
                  <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-xs text-gray-600 leading-relaxed">
                    {status === "approved" && "Approved by the Membership Committee."}
                    {status === "rejected" && "This application was rejected — the decision is final and cannot be reopened."}
                    {status === "returned" && "Returned to the applicant for editing — awaiting their correction and resubmission."}
                    {status === "pending" && "The applicant has not yet completed and submitted this application."}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageGate>
  );
}

/**
 * The application-fee receipt.
 *
 * Read off the applicant's SIGNUP invoice, which is where the fee actually
 * lives — it is raised when the account is created, long before the
 * application exists, and the two are joined only by the user.
 *
 * Shows what a reviewer would check against a bank statement or quote back to
 * an applicant: the reference, the amount, the date and how it was taken. The
 * old page could only say paid or unpaid, which settles no argument.
 */
function FeeReceipt({ receipt, settled }: { receipt: any; settled?: boolean }) {
  // No invoice at all. Older accounts predate the fee being raised at signup,
  // and saying "unpaid" about them would be an accusation the record does not
  // support.
  if (!receipt) {
    return (
      <div className="rounded-2xl border bg-white p-4 text-sm text-gray-500">
        <span className="font-semibold text-gray-700">Application fee</span> — no invoice is on
        record for this applicant{settled ? ", though their account is marked as having paid" : ""}.
      </div>
    );
  }

  const paid = !!receipt.settled;
  const money = (v: any) =>
    v === null || v === undefined
      ? "—"
      : `${receipt.currency ?? "NGN"} ${Number(v).toLocaleString("en-NG", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;

  return (
    <div
      className={`rounded-2xl border p-5 ${
        paid ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p
            className={`text-[10px] font-black uppercase tracking-widest ${
              paid ? "text-emerald-700" : "text-amber-700"
            }`}
          >
            Application fee
          </p>
          <p className={`text-lg font-bold ${paid ? "text-emerald-900" : "text-amber-900"}`}>
            {paid ? "Paid" : "Not settled"}
            {paid && receipt.recorded_offline ? " · recorded by hand" : ""}
          </p>
        </div>
        <p className={`text-2xl font-black ${paid ? "text-emerald-800" : "text-amber-800"}`}>
          {money(paid ? receipt.amount_paid : receipt.amount)}
        </p>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <Field label="Receipt no." value={receipt.receipt_number} />
        <Field label="Reference" value={receipt.reference} />
        <Field label="Paid on" value={fmtDate(receipt.paid_at)} />
        <Field label="Method" value={receipt.method ?? receipt.gateway} />
        <Field label="Invoice no." value={receipt.invoice_number} />
        <Field label="Invoiced" value={fmtDate(receipt.invoiced_at)} />
        <Field label="Invoice status" value={receipt.invoice_status} />
        {/* Only where something is actually outstanding — a zero balance on a
            settled invoice is noise. */}
        {Number(receipt.balance) > 0 && (
          <Field label="Outstanding" value={money(receipt.balance)} />
        )}
      </div>

      {/* Said plainly, because the two readings are different: the gateway
          confirming money, or somebody in the office saying it arrived. */}
      {paid && receipt.recorded_offline && (
        <p className="mt-3 text-[11px] text-emerald-800">
          Settled against the invoice without a gateway payment — an offline or bank transfer
          recorded by the secretariat.
        </p>
      )}
    </div>
  );
}
